import { last } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FieldObject } from '../../../../openapi/atlantis';
import { FileResponse } from '../../../../openapi/dropbox';
import { ChecklistApi, FileReferenceDto } from '../../../../openapi/sherlock';
import ErrorService from '../../../../services/ErrorService';
import { useAppDispatch, useAppSelector } from '../../../../slices/store';
import { showErrorToast } from '../../../../slices/ToastNotificationSlice';
import {
  convertFileToFileResponse,
  getDropboxFilesFromFileReferences,
} from '../../../../utils/DropboxUtils';
import { getSherlockConfiguration } from '../../../../utils/OpenapiConfigurationUtils';
import { getInputValidations } from '../../../../utils/Validations';
import ControlledSingleFileInputV2 from '../../../inputs/V2/ControlledSingleFileInputV2';
import FileRowV2 from './FileRowV2';

interface SingleFileInputCardProps {
  name: string;
  field: FieldObject;
  checklistItemId: string;
}

const SingleFileInputCard: React.FC<SingleFileInputCardProps> = ({
  name,
  field,
  checklistItemId,
}) => {
  const dispatch = useAppDispatch();
  const { control, setValue } = useFormContext();
  const {
    dropbox: { dropboxByBorrowerId },
    auth: { authUserDetail },
  } = useAppSelector((state) => ({
    dropbox: state.dropbox,
    auth: state.auth,
  }));
  const dropbox = dropboxByBorrowerId[authUserDetail?.id!];
  const [fileReferences, setFileReferences] = useState<FileReferenceDto[]>();
  const [uploadedFile, setUploadedFile] = useState<FileResponse>();

  const fetchChecklistItemById = useCallback(
    async (id: string) => {
      try {
        const { data } = await new ChecklistApi(
          await getSherlockConfiguration(),
        ).getChecklistItemById(id);
        setFileReferences(data.fileReferences?.references || []);
      } catch (e: any) {
        dispatch(
          showErrorToast(
            'Unable to fetch the checklist item. Please try again after sometime.',
          ),
        );
        ErrorService.notify('Unable to fetch checklist item', e, {
          data: {
            checklistItemId: id,
          },
        });
      }
    },
    [dispatch],
  );

  useEffect(() => {
    fetchChecklistItemById(checklistItemId);
  }, [checklistItemId, fetchChecklistItemById]);

  const files = useMemo(
    () => getDropboxFilesFromFileReferences(fileReferences || [], dropbox!),
    [fileReferences, dropbox],
  );

  const fileFieldValue = useMemo(() => {
    return last(files) || uploadedFile;
  }, [files, uploadedFile]);

  return (
    <div>
      {!(fileFieldValue?.id || fileFieldValue?.filename) ? (
        <ControlledSingleFileInputV2
          name={name}
          control={control}
          rules={getInputValidations(field)}
          onChangeSpy={(file) => {
            setValue(name, file, { shouldValidate: true });
            setUploadedFile(convertFileToFileResponse(file));
          }}
        />
      ) : (
        <div
          className='mt-2'
          key={fileFieldValue?.id || fileFieldValue?.filename}
        >
          <FileRowV2
            file={fileFieldValue}
            onDelete={() => {
              setUploadedFile(undefined);
              setValue(name, undefined);
              setFileReferences(undefined);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SingleFileInputCard;
